import React, { useState, useEffect } from "react";

export default function CircularGallery({
  items,
  renderCard,
  visibleCount = 5,
}) {
  const [center, setCenter] = useState(0);
  const [screenSize, setScreenSize] = useState("desktop");
  const N = items.length;
  const maxVisible = Math.min(visibleCount, N);

  // Helper to wrap indices
  const getIndex = (idx) => (idx + N) % N;

  // Update screen size on resize
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setScreenSize("mobile");
      } else if (width < 1024) {
        setScreenSize("tablet");
      } else {
        setScreenSize("desktop");
      }
    };

    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  // Animation/positioning parameters - responsive card sizes
  const getCardDimensions = () => {
    switch (screenSize) {
      case "mobile":
        return {
          cardWidth: 200,
          cardHeight: 280,
          gap: 15,
          visibleCount: 3,
        };
      case "tablet":
        return {
          cardWidth: 240,
          cardHeight: 320,
          gap: 25,
          visibleCount: 4,
        };
      default:
        return {
          cardWidth: 280,
          cardHeight: 360,
          gap: 35,
          visibleCount: 5,
        };
    }
  };

  const {
    cardWidth,
    cardHeight,
    gap,
    visibleCount: currentVisibleCount,
  } = getCardDimensions();
  const scaleStep = 0.15;
  const rotateStep = 18;
  const opacityStep = 0.25;

  // Only render visible cards
  const visible = [];
  for (
    let offset = -Math.floor(currentVisibleCount / 2);
    offset <= Math.floor(currentVisibleCount / 2);
    offset++
  ) {
    let i = getIndex(center + offset);
    visible.push({
      item: items[i],
      index: i,
      offset,
    });
  }

  return (
    <div className="relative w-full h-[250px] sm:h-[300px] md:h-[400px] lg:h-[500px] xl:h-[550px] flex items-center justify-center select-none overflow-hidden">
      {/* Left nav */}
      <button
        className="absolute left-1 sm:left-2 md:left-4 z-10 bg-gray-900/60 hover:bg-orange-400/30 transition-colors rounded-full p-1.5 sm:p-2 md:p-3 top-1/2 -translate-y-1/2 border border-gray-700"
        onClick={() => setCenter((c) => getIndex(c - 1))}
        aria-label="Previous"
        style={{ pointerEvents: "auto" }}
      >
        <svg
          width={16}
          height={16}
          className="sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7"
          fill="none"
          stroke="orange"
          strokeWidth={3}
          viewBox="0 0 24 24"
        >
          <path d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      {/* Cards */}
      <div
        className="relative flex items-center justify-center w-full h-full"
        style={{ perspective: 1400 }}
      >
        {visible.map(({ item, index, offset }) => {
          // Main card in center
          const isCenter = offset === 0;
          // Style for animation
          const scale = 1 - Math.abs(offset) * scaleStep;
          const rotate = offset * rotateStep;
          const opacity = 1 - Math.abs(offset) * opacityStep;
          const x = offset * (cardWidth + gap) * 0.5;
          return (
            <div
              key={index}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: cardWidth,
                height: cardHeight,
                transform: `translate(-50%, -50%) translateX(${x}px) scale(${scale}) rotateZ(${rotate}deg)`,
                zIndex: 100 - Math.abs(offset),
                opacity,
                boxShadow: isCenter
                  ? "0 8px 32px 0 rgba(255,140,0,0.18)"
                  : "0 2px 12px 0 rgba(0,0,0,0.08)",
                transition:
                  "transform 0.7s cubic-bezier(.83,0,.17,1), opacity 0.4s cubic-bezier(.83,0,.17,1)",
                pointerEvents: isCenter ? "auto" : "none",
                cursor: isCenter ? "grab" : "default",
              }}
            >
              {renderCard(item, index)}
            </div>
          );
        })}
      </div>

      {/* Right nav */}
      <button
        className="absolute right-1 sm:right-2 md:right-4 z-10 bg-gray-900/60 hover:bg-orange-400/30 transition-colors rounded-full p-1.5 sm:p-2 md:p-3 top-1/2 -translate-y-1/2 border border-gray-700"
        onClick={() => setCenter((c) => getIndex(c + 1))}
        aria-label="Next"
        style={{ pointerEvents: "auto" }}
      >
        <svg
          width={16}
          height={16}
          className="sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7"
          fill="none"
          stroke="orange"
          strokeWidth={3}
          viewBox="0 0 24 24"
        >
          <path d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}
